import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkerDetail } from '@/services/workersV2API';

type Props = {
  worker: WorkerDetail;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatStatus = (status: string | null | undefined) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    style={{
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #F3F4F6',
    }}
  >
    <Small color="lighter">{label}</Small>
    <Body>{value}</Body>
  </Stack>
);

const WorkerDetailsCard = ({ worker }: Props) => {
  const address = worker.address;
  const fullAddress = [
    address.address_line1,
    address.address_line2,
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Card title="Worker Details">
      <Card.Section>
        <Stack vertical gap={4}>
          <DetailRow label="Worker ID" value={worker.id} />
          <DetailRow label="Email" value={worker.user.email} />
          <DetailRow label="Phone" value={worker.user.phone_number || 'N/A'} />
          <DetailRow
            label="Date of Birth"
            value={formatDate(worker.user.date_of_birth)}
          />
          <DetailRow
            label="Language"
            value={worker.user.language?.toUpperCase() || 'N/A'}
          />
          <DetailRow label="Address" value={fullAddress || 'N/A'} />
          <DetailRow
            label="Tax Type"
            value={
              <Badge
                label={worker.tax_type.toUpperCase()}
                status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
              />
            }
          />
          <DetailRow
            label="Active"
            value={
              <Badge
                label={worker.active ? 'Enabled' : 'Disabled'}
                status={worker.active ? 'theme' : 'danger'}
              />
            }
          />
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Statuses</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Onboarding"
              value={
                <Badge
                  label={formatStatus(worker.statuses.onboarding_status)}
                  status={
                    worker.statuses.onboarding_status === 'onboarding_completed'
                      ? 'theme'
                      : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="I-9"
              value={
                <Badge
                  label={formatStatus(worker.statuses.i9_status)}
                  status={
                    worker.statuses.i9_status === 'approved'
                      ? 'theme'
                      : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="Background"
              value={
                <Badge
                  label={formatStatus(worker.statuses.background_status)}
                  status={
                    worker.statuses.background_status === 'approved'
                      ? 'theme'
                      : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="Ready to Work"
              value={
                <Badge
                  label={
                    worker.statuses.active_and_ready_to_work ? 'Yes' : 'No'
                  }
                  status={
                    worker.statuses.active_and_ready_to_work
                      ? 'theme'
                      : 'neutral'
                  }
                />
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Stats</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Jobs Worked" value={worker.stats.jobs_worked} />
            <DetailRow label="Jobs Dropped" value={worker.stats.jobs_dropped} />
            <DetailRow label="Jobs Failed" value={worker.stats.jobs_failed} />
            <DetailRow label="No Shows" value={worker.stats.no_show} />
            {worker.ratings.average_rating && (
              <DetailRow
                label="Average Rating"
                value={`${worker.ratings.average_rating.toFixed(1)} (${worker.ratings.jobs_rating_count} reviews)`}
              />
            )}
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Dates</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Created" value={formatDate(worker.created_at)} />
            <DetailRow
              label="Onboarding Completed"
              value={formatDate(worker.dates.onboarding_completed_at)}
            />
            <DetailRow
              label="Approved"
              value={formatDate(worker.dates.approved_at)}
            />
            <DetailRow
              label="T&C Accepted"
              value={formatDate(worker.dates.tc_accepted_at)}
            />
          </Stack>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default WorkerDetailsCard;
